import {
  SPORTS,
  GENDERS,
  TIERS,
  REGIONS_BY_TIER,
  NCAA_CONFERENCE_GROUPS,
  ANY_LEAGUE_BODY,
  governingBodiesFor,
  subDivisionsFor,
  classOptionsFor,
  buildCerebroCompetitionBody,
  buildCachePartnerBody,
  resolveFetchPlan,
} from "../../../ForBusiness/Cacheshare/taxonomy";
import { COACH_VERIFICATION_PATH } from "../coachPaths";
import { displayTeamName, stripSeasonSuffix } from "../../../../utils/teamNames";

// Coach onboarding V2 (Figma "Coach Onboarding", frames 05 to 09): the
// wizard's steps, the cascade rules, the draft saved on the server and the
// team binding the submission sends. Pure, unit-tested in
// coachOnboardingModel.test.js.

export const WIZARD_STEPS = ["sport", "tier", "league", "team", "teamRequest"];
// Saved once the team is submitted; the coach then waits on the awaiting
// verification screen (COACH_VERIFICATION_PATH).
export const AWAITING = "awaiting";

// The name step (sign-up) is Step 1 of 4.
export const TOPBAR = {
  sport: { progress: { step: 2, total: 4 } },
  tier: { progress: { step: 2, total: 4 } },
  league: { progress: { step: 3, total: 4 }, skip: true },
  team: { progress: { step: 4, total: 4 } },
  teamRequest: { progress: { step: 4, total: 4 } },
};
// The account exists from the name step on, so Sport has no way back.
export const BACK = { tier: "sport", league: "tier", team: "league", teamRequest: "team" };

// The cascade in order: changing a pick clears every pick below it, the
// team search and (in the wizard) the picked team.
const CASCADE = ["sport", "gender", "tier", "region", "body", "subDivision", "classDivision", "conference"];
export const EMPTY_FILTERS = { ...Object.fromEntries(CASCADE.map((k) => [k, null])), teamSearch: "" };

const keyOf = (item) => (item == null ? null : item.value ?? item.name ?? null);

// -> {filters, scopeChanged}
export const changeFilters = (prev, patch) => {
  const next = { ...prev, ...patch };
  const at = CASCADE.findIndex((k) => keyOf(prev[k]) !== keyOf(next[k]));
  if (at < 0) return { filters: next, scopeChanged: false };
  const cleared = Object.fromEntries(CASCADE.slice(at + 1).map((k) => [k, null]));
  return { filters: { ...next, ...cleared, teamSearch: "" }, scopeChanged: true };
};

export const sportReady = (f) => !!f.sport && (!f.sport.hasGender || !!f.gender);

// Region and League are optional: a tier alone lists teams (College unions
// every governing body).
export const canContinue = (step, f) => {
  if (step === "sport") return sportReady(f);
  if (step === "tier") return sportReady(f) && !!f.tier;
  return true;
};

// A saved step the draft cannot support yet opens on the first incomplete one.
export const reachableStep = (step, f) => {
  if (!sportReady(f)) return "sport";
  if (step !== "sport" && !f.tier) return "tier";
  return step;
};

// Where the wizard opens for the state GET coach/onboarding/ answers.
// -> {step} or {redirect}. Only an onboarded account is sent on: the route
// guards send anyone else back here.
export const openingStep = (state) => {
  const status = state?.verification?.status;
  const saved = state?.progress?.step;
  const inWizard = WIZARD_STEPS.includes(saved);
  if (state?.onboardingCompleted) {
    if (status === "verified") return { redirect: "/coach/home" };
    // A submitted coach waits for the admin unless they reopened the wizard.
    if (["pending", "rejected"].includes(status) && !inWizard) return { redirect: COACH_VERIFICATION_PATH };
  }
  return { step: inWizard ? saved : "sport" };
};

export const planFor = (f) =>
  resolveFetchPlan({
    sportValue: f.sport?.value,
    gender: f.gender?.value,
    tier: f.tier?.value,
    region: f.region?.value,
    body: f.body,
    // League is optional, so a College coach who skips it sees every college team.
    collegeUnion: true,
    subDivision: f.subDivision,
    classDivision: f.classDivision,
    conference: f.conference?.value,
    teamSearch: f.teamSearch,
  });

// "17U" in "Search teams in 17U..." and "34 teams in 17U".
export const scopeLabel = (f) => {
  if (f.body?.anyLeague) return "every league";
  return f.conference?.name || f.subDivision?.name || f.body?.name || f.region?.name || f.tier?.name || "";
};

// SportRadar names a school by market ("Duke") and mascot ("Blue Devils").
export const teamName = (team) => (team?.srSport ? [team.market, team.name].filter(Boolean).join(" ") : team?.name || "");

// The name the team list, the "Selected:" line and the Continue button show:
// the one display rule for every other team (utils/teamNames: the row's
// displayName, else its name with the age group of its league key and no
// season), so a program's 17U, 16U and 15U squads read apart ("Oakland
// Soldiers 16U", 10/19 notes, D15). teamName stays the name that is saved.
export const teamTitle = (team) => (team?.srSport ? teamName(team) : displayTeamName(team) || teamName(team));

// A league label reads without the season CACHE adds to it ("Nike EYCL
// (Girls) 17U", never "... · 2026": 10/19 notes, D15).
export const teamMeta = (team, f) =>
  stripSeasonSuffix(team.leagueName || "") ||
  team.conference ||
  team.location ||
  [f.body?.anyLeague ? "" : f.body?.name, f.subDivision?.name].filter(Boolean).join(" ") ||
  f.tier?.name ||
  "";

export const teamInitials = (name) =>
  (
    String(name || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("") || "?"
  ).toUpperCase();

// One Cerebro program is several squads (17U, 16U...) sharing an id, so the
// squad scope is part of a team's identity.
export const teamKey = (team) => (team ? [team.source, team.id, team.leagueKey || team.cacheLeague].map((v) => v ?? "").join("|") : "");

// The league key and roster source the submission sends for a picked team.
// A Cerebro list tags every team with its fetch plan's first key, so a
// Cerebro squad also sends its own "competition|division" (leagueKey), which
// the server binds the team to.
const SR_LEAGUE = { mbb: "ncaamb", wbb: "ncaawb" };
export const teamBinding = (team) => {
  if (!team) return { league: "", leagueKey: "", teamSource: "" };
  if (team.source === "genius") return { league: "ncaafb", leagueKey: "", teamSource: "genius" };
  return {
    league: team.cacheLeague || team.cerebroLeague || SR_LEAGUE[team.srSport] || "",
    leagueKey: team.source === "cerebro" ? team.leagueKey || "" : "",
    // Blank lets the server read the source off the league key.
    teamSource: team.source || (team.srSport ? "sportsradar" : ""),
  };
};

// The binding a picked team row is filed under, for the admin dialogs that
// bind an athlete or a coach to a team (M-CODES 1.3a): `league`, a partner
// team's CACHE league, a Cerebro squad's own catalogue key, a SportsRadar
// or Genius team's league, else the scope's key (a team list tags every
// Cerebro row with its fetch plan's first key, which names another squad
// of a multi-squad scope); `leagueKey`, the squad's own
// "competition|division" the server files a Cerebro squad by; the source
// the row comes from (never the fetch plan's "multi"); and `programOnly`, a
// Cerebro program with no competition, which the server binds through a
// provisional CACHE team (D7). A row the server marks programOnly false
// with no league of its own (its competitions could not be checked while
// the team feed was down) is neither: it has no league to file it under
// until the feed answers. `league` is also what the roster is read under.
export const pickedTeamBinding = (row, plan) => {
  if (!row) return { league: "", leagueKey: "", teamSource: "", programOnly: false };
  if (row.cacheLeague || row.source === "cache_teams") {
    return { league: row.cacheLeague || row.league_key || "", leagueKey: "", teamSource: "cache_teams", programOnly: false };
  }
  if (row.source === "genius") return { league: "ncaafb", leagueKey: "", teamSource: "genius", programOnly: false };
  if (row.srSport || (!row.source && plan?.dataSource === "sportsradar")) {
    const sport = row.srSport || plan?.srSport || "";
    return { league: SR_LEAGUE[sport] || sport, leagueKey: "", teamSource: "sportsradar", programOnly: false };
  }
  // "Any league" lists programs by name: its scope key names no squad.
  const scope = row.cerebroLeague || (plan?.dataSource === "cerebro" ? (plan.cerebroSports || [])[0] : "") || "";
  const league = row.programOnly === true ? "" : (row.catalogueKey || (scope === "cbx_any" ? "" : scope));
  return { league, leagueKey: row.leagueKey || "", teamSource: "cerebro", programOnly: !league && row.programOnly !== false };
};

// "Under Armour Association 17U" on 09: the team's own league, else the
// governing body and circuit the coach picked.
export const leagueOf = (team) => stripSeasonSuffix(team?.leagueLabel || "") || [team?.body, team?.subDivision].filter(Boolean).join(" ");

// The labels stored on the coach's verification record (Team settings).
export const cascadeLabels = (f, team) => ({
  sportLabel: f.sport?.name || "",
  genderLabel: f.gender?.name || "",
  tierLabel: f.tier?.name || "",
  regionLabel: f.region?.name || "",
  bodyLabel: f.body && !f.body.anyLeague ? f.body.name : "",
  subDivisionLabel: f.subDivision?.name || "",
  classDivisionLabel: f.classDivision?.name || "",
  conferenceLabel: f.conference?.name || "",
  // The team's own league name, which the server also reads the true league
  // key from. Stored without the season, so it never goes stale next season.
  leagueDisplayName: stripSeasonSuffix(team?.leagueName || ""),
});

// "Can't find your team" needs all three; outside North America the state
// is a region or province typed in.
export const requestReady = (r) => !!(r && r.teamName?.trim() && r.city?.trim() && r.state?.trim());
export const usesUsStates = (f) => !f.region || f.region.value === "na";

// 09's Team settings card for the submitted team (GET coach/onboarding/
// `team`), in the Figma order; rows without a value are left out.
export const verificationRows = (team) =>
  team
    ? [
        { label: "Sport", value: [team.sport, team.gender].filter(Boolean).join(" · ") },
        { label: "Tier", value: team.tier },
        { label: "Region", value: team.region },
        { label: "League", value: leagueOf(team) },
        { label: "Current team", value: team.name, requested: !!team.requested },
      ].filter((row) => row.value)
    : [];

// ---------------------------------------------------------------- draft

const pick = (obj, keys) => {
  const out = {};
  keys.forEach((k) => {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") out[k] = typeof v === "number" ? String(v) : v;
  });
  return out;
};
const TEAM_FIELDS = ["id", "name", "displayName", "ageGroup", "market", "source", "cerebroLeague", "cacheLeague", "srSport", "leagueName", "leagueKey", "conference", "location"];
const ENTRY_FIELDS = ["key", "label", "name", "level", "gender"];
const REQUEST_FIELDS = ["teamName", "city", "state"];

const bodyDraft = (body) => {
  if (!body) return undefined;
  if (body.catalogue) return { kind: "catalogue", value: body.value, name: body.name, entry: pick(body.catalogue, ENTRY_FIELDS) };
  if (body.dataSource === "cache_teams") return { kind: "partner", value: body.value, name: body.name, partnerProgram: body.partnerProgram };
  return { kind: "static", value: body.value, name: body.name };
};

const bodyFromDraft = (d, f) => {
  if (!d?.value) return null;
  if (d.kind === "catalogue") return d.entry?.key ? buildCerebroCompetitionBody(d.entry) : null;
  if (d.kind === "partner") return buildCachePartnerBody({ partner_program: d.partnerProgram || d.name });
  if (d.value === ANY_LEAGUE_BODY.value) return ANY_LEAGUE_BODY;
  return governingBodiesFor({ tier: f.tier?.value, region: f.region?.value, gender: f.gender?.value, sport: f.sport?.value }).find((b) => b.value === d.value) || null;
};

const CONFERENCES = (NCAA_CONFERENCE_GROUPS || []).flatMap((g) => g.options);

// What PATCH coach/onboarding/ stores: values and labels only.
export const toDraft = (f, team, request) => {
  const draft = {
    ...pick({ sport: f.sport?.value, gender: f.gender?.value, tier: f.tier?.value, region: f.region?.value }, ["sport", "gender", "tier", "region"]),
    ...pick({ subDivision: f.subDivision?.value, classDivision: f.classDivision?.name, conference: f.conference?.value, teamSearch: f.teamSearch }, ["subDivision", "classDivision", "conference", "teamSearch"]),
  };
  const body = bodyDraft(f.body);
  if (body) draft.body = body;
  if (team) draft.team = pick(team, TEAM_FIELDS);
  const req = pick(request, REQUEST_FIELDS);
  if (Object.keys(req).length) draft.request = req;
  return draft;
};

// The cascade picks back as taxonomy objects. A pick that no longer exists
// (or whose parent is missing) is dropped with everything below it.
export const filtersFromDraft = (d = {}) => {
  const sport = SPORTS.find((s) => s.value === d.sport && s.status === "live") || null;
  const gender = sport?.hasGender ? GENDERS.find((g) => g.value === d.gender) || null : null;
  if (!sportReady({ sport, gender })) return { ...EMPTY_FILTERS, sport };
  const tier = TIERS.find((t) => t.value === d.tier) || null;
  if (!tier) return { ...EMPTY_FILTERS, sport, gender };
  const region = (REGIONS_BY_TIER[tier.value] || []).find((r) => r.value === d.region) || null;
  const base = { ...EMPTY_FILTERS, sport, gender, tier, region, teamSearch: d.teamSearch || "" };
  const body = bodyFromDraft(d.body, base);
  const subDivision = body ? subDivisionsFor({ body, gender: gender?.value }).find((s) => s.value === d.subDivision) || null : null;
  const cls = classOptionsFor(subDivision).find((c) => c.name === d.classDivision);
  const classDivision = cls ? { value: cls.name, name: cls.name, cerebroSports: cls.cerebroSports } : null;
  const conference = tier.value === "college" && body?.value === "ncaa" ? CONFERENCES.find((c) => c.value === d.conference) || null : null;
  return { ...base, body, subDivision, classDivision, conference };
};

// The server's reason for a failed request (axios keeps it on response.data).
// A request the server could not answer (a 5xx) never shows its body's
// text, which can carry a data vendor's name or a raw error (10/19 notes,
// D16): CACHE's own sentence when team data is down (code
// provider_unavailable), else the fallback.
export const errorMessage = (e, fallback) => {
  const data = e?.response?.data;
  const body = data && typeof data === "object" ? data : {};
  if (e?.response?.status >= 500) {
    const neutral = body.code === "provider_unavailable" ? body.error || body.message : "";
    return (typeof neutral === "string" && neutral.trim()) || fallback;
  }
  const reason = body.message || body.error;
  return (typeof reason === "string" && reason) || fallback;
};
